import * as React from 'react';
import {
  GridColDef, GridColumnVisibilityModel, GridFilterModel, GridLogicOperator, GridRowModel,
  GridRowSelectionModel, GridSortModel, GridToolbar, GridToolbarColumnsButton, GridToolbarContainer,
  GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery } from '@tanstack/react-query';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { DeleteDiaglog } from '../components/DeleteDialog';
import { FilterModel } from 'bloggers-backend/lib/domain/Repo';
import { emptySelection, selectedRowIds } from './selectedRowIds';
import { useDeleteMany } from '../components/forms/hooks/useDeleteMany';


interface PageInfo {
  totalRowCount?: number;
  nextCursor?: string;
  pageSize?: number;
}

export interface QueryOptions {
  cursor?: string;
  page?: number;
  pageSize?: number;
  filterModel?: FilterModel;
  sortModel?: GridSortModel;
}

export interface QueryResult {
    pageInfo: PageInfo;
    data: GridRowModel[];
}

export interface BulkDelete {
  event: string;
  deleteWhat: string;
}

interface QuickFilterProps {
  debounceMs?: number;
}

interface BulkDeleteToolbarProps {
  showQuickFilter?: boolean;
  quickFilterProps?: QuickFilterProps;
  selectionCount: number;
  deleteWhat: string;
  deleteAction: () => void;
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends Partial<BulkDeleteToolbarProps> {}
}

function BulkDeleteToolbar({
  showQuickFilter, quickFilterProps, selectionCount = 0, deleteWhat = '', deleteAction = () => undefined,
}: Partial<BulkDeleteToolbarProps>) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      {selectionCount > 0 && (
        <>
          <DeleteDiaglog
            isOpen={deleteDialogOpen}
            deleteWhat={deleteWhat}
            userRejects={() => setDeleteDialogOpen(false)}
            userAccepts={() => {
              setDeleteDialogOpen(false);
              deleteAction();
            }}
          />
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete {selectionCount}
          </Button>
        </>
      )}
      <Box sx={{ flex: 1 }} />
      {showQuickFilter && <GridToolbarQuickFilter {...quickFilterProps} />}
    </GridToolbarContainer>
  );
}

export interface GridProps {
  columns: GridColDef[];
  cacheName: string;
  onRowClick: (id: string | number) => void;
  fetchData: (queryOptions: QueryOptions) => Promise<QueryResult>;
  showSearch?: boolean
  initialFilterModel?: GridFilterModel;
  initialSortModel?: GridSortModel;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  initialPageSize?: number;
  baseFilterModel?: GridFilterModel;
  bulkDelete?: BulkDelete;
}


function toBackendFilterModel(model: GridFilterModel): FilterModel {
  return {
    items: model.items.map(({ field, operator, value }) => ({ columnField: field, operatorValue: operator, value })),
    quickFilterValues: model.quickFilterValues,
    linkOperator: model.logicOperator,
  };
}

export default function Grid({
  columns, fetchData, onRowClick, cacheName, showSearch,
  initialFilterModel, initialSortModel, initialColumnVisibilityModel, initialPageSize, baseFilterModel, bulkDelete,
}: GridProps) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(initialPageSize ?? 10);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    initialFilterModel ?? { items: [], logicOperator: GridLogicOperator.And }
  );

  const [sortModel, setSortModel] = React.useState<GridSortModel>(initialSortModel ?? []);

  const [selection, setSelection] = React.useState<GridRowSelectionModel>(emptySelection);

  const {deleting, deleteEntities} = useDeleteMany(bulkDelete?.event ?? '', cacheName);

  const columsWithFilters = columns;

  const mapPageToNextCursor = React.useRef<{[page: number]: string | undefined}>({0: undefined});

  const { data, isFetching } = useQuery([cacheName, page, pageSize, filterModel, sortModel], (query) => {
    const page = query.queryKey[1] as number;
    const pageSize = query.queryKey[2] as number;
    const cursor = mapPageToNextCursor.current[page - 1];

    const requestFilterModel = toBackendFilterModel(baseFilterModel
      ? {...filterModel, items: [...baseFilterModel.items, ...filterModel.items]}
      : filterModel);

    return fetchData({page, pageSize, cursor, filterModel: requestFilterModel, sortModel}).then((data) => {
      mapPageToNextCursor.current[page] = data.pageInfo.nextCursor
      return data
    });
  }, { keepPreviousData: true })

  async function deleteSelection() {
    await deleteEntities(selectedRowIds(selection, data?.data ?? []).map(String));
    setSelection(emptySelection());
  }

  const quickFilterProps = { debounceMs: 500 };

  const selectionCount = selectedRowIds(selection, data?.data ?? []).length;

  const toolbarProps = bulkDelete
    ? {
      showQuickFilter: showSearch,
      quickFilterProps,
      selectionCount,
      deleteWhat: `batch of ${selectionCount} ${bulkDelete.deleteWhat}`,
      deleteAction: () => { void deleteSelection(); },
    }
    : { showQuickFilter: showSearch, quickFilterProps };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <DataGridPro
        showToolbar
        slots={{ toolbar: bulkDelete ? BulkDeleteToolbar : GridToolbar }}
        rows={data?.data || []}
        columns={columsWithFilters}
        rowCount={data?.pageInfo.totalRowCount || 0}
        loading={isFetching || deleting}
        checkboxSelection={Boolean(bulkDelete)}
        rowSelectionModel={selection}
        onRowSelectionModelChange={(model) => setSelection(model)}
        pageSizeOptions={[10, 20, 50, 100, 500, 1000, 5000, 10000, 100000]}
        pagination
        paginationModel={{ page, pageSize }}
        initialState={{ columns: { columnVisibilityModel: initialColumnVisibilityModel } }}
        paginationMode="server"
        sortingMode="server"
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        slotProps={{
          baseSelect: { native: true },
          filterPanel: {
            logicOperators: [GridLogicOperator.And],
          },
          toolbar: toolbarProps,
        }}
        filterMode={"server"}
        onFilterModelChange={(model) => {
          setPage(0);
          mapPageToNextCursor.current = {0: undefined}
          setFilterModel(model);
        }}
        onSortModelChange={(model) => {
          setPage(0);
          mapPageToNextCursor.current = {0: undefined}
          setSortModel(model);
        }}
        onRowClick={(event) => onRowClick(event.id)}
      />
    </Box>
  );
}
